import { useEffect, useRef, useState } from 'react';
import { CircularProgressbar, buildStyles } from 'react-circular-progressbar';
import 'react-circular-progressbar/dist/styles.css';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/components/ui/tooltip';
import { SaleStep } from '@/utils/sale/steps';

// The arc covers 3/4 of the circle, starting 5/8 of a turn clockwise from the top.
const CIRCLE_RATIO = 0.75;
const ROTATION = 1 / 2 + 1 / 8;
// The default stroke width 8 in the 100×100 viewBox puts the track's centre at radius 46.
const RADIUS = 46;
const PATH_COLOR = '#702a81';
const BONUS_COLOR = '#16a34a';

interface CircularProgressBarProps {
  unitCount: number;
  unitCountTarget: number;
  /** Shown as dots on the track when within the target, with a label on hover, focus or tap. */
  steps?: SaleStep[];
}

export default function CircularProgressBar({ unitCount, unitCountTarget, steps = [] }: CircularProgressBarProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState<number | null>(null);

  // A tap elsewhere closes the label, since touch has no hover to leave.
  useEffect(() => {
    if (active === null) return;
    const close = (e: PointerEvent) => !ref.current?.contains(e.target as Node) && setActive(null);
    document.addEventListener('pointerdown', close);
    return () => document.removeEventListener('pointerdown', close);
  }, [active]);

  const dots = steps
    .filter((step) => step.itemCount < unitCountTarget)
    .map((step) => {
      const angle = 2 * Math.PI * (ROTATION + CIRCLE_RATIO * (step.itemCount / unitCountTarget));
      return { step, x: 50 + RADIUS * Math.sin(angle), y: 50 - RADIUS * Math.cos(angle) };
    });

  return (
    <div className="p-4">
      <div ref={ref} className="relative">
        <CircularProgressbar
          value={unitCount}
          text={`${unitCount} / ${unitCountTarget} st`}
          maxValue={unitCountTarget}
          circleRatio={CIRCLE_RATIO}
          className="block font-outfit"
          styles={buildStyles({
            rotation: ROTATION,
            trailColor: '#eee',
            pathColor: PATH_COLOR,
            textColor: '#000',
            textSize: 8,
          })}
        />
        {dots.length > 0 && (
          <svg viewBox="0 0 100 100" className="absolute inset-0 size-full overflow-visible">
            {dots.map(({ step, x, y }) => {
              const color = step.kind === 'bonus' ? BONUS_COLOR : PATH_COLOR;
              const isActive = step.itemCount === active;

              return (
                // Same tooltip as the sidebar links, opened by our own state so a tap opens it too.
                <Tooltip key={step.itemCount} open={isActive} onOpenChange={() => undefined}>
                  <TooltipTrigger asChild>
                    <g
                      role="button"
                      tabIndex={0}
                      aria-label={`${step.label}. ${stepStatus(step, unitCount)}`}
                      className="cursor-pointer outline-none"
                      onPointerEnter={(e) => e.pointerType === 'mouse' && setActive(step.itemCount)}
                      onPointerLeave={(e) => e.pointerType === 'mouse' && setActive(null)}
                      // A mouse already opened it on hover, so only taps toggle.
                      onPointerUp={(e) => e.pointerType !== 'mouse' && setActive(isActive ? null : step.itemCount)}
                      onFocus={() => setActive(step.itemCount)}
                      onBlur={() => setActive(null)}
                    >
                      {/* Larger invisible hit area, easier to tap */}
                      <circle cx={x} cy={y} r={7} fill="transparent" />
                      <circle
                        cx={x}
                        cy={y}
                        r={isActive ? 3.4 : 2.6}
                        fill={step.reached ? color : '#fff'}
                        stroke={step.reached ? '#fff' : color}
                        strokeWidth={1.2}
                        className="transition-[r]"
                      />
                    </g>
                  </TooltipTrigger>
                  <TooltipContent side="top">
                    <p className="font-medium">{step.label}</p>
                    <p>{stepStatus(step, unitCount)}</p>
                  </TooltipContent>
                </Tooltip>
              );
            })}
          </svg>
        )}
      </div>
    </div>
  );
}

/** "Vid 100 st · 40 kvar" or "Vid 100 st · Uppnått". */
const stepStatus = (step: SaleStep, unitCount: number) =>
  `Vid ${step.itemCount.toLocaleString('sv-SE')} st · ${
    step.reached ? 'Uppnått' : `${(step.itemCount - unitCount).toLocaleString('sv-SE')} kvar`
  }`;
